import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styles from './Launch.module.css';

export default function Launch() {
  return (
    <div className={styles.container}>
      <Helmet>
        <title>MyPeopleDatabase — The Personal CRM for Real Relationships</title>
        <meta
          name="description"
          content="MyPeopleDatabase is a desktop app that gives you a private, organized system for every person in your life. Groups, notes, reminders, AI search, full data ownership. Available for Mac and Windows."
        />
      </Helmet>

      <div className={styles.content}>
        {/* ── Hero ── */}
        <span className={styles.badge}>Now Available for Mac &amp; Windows</span>
        <h1 className={styles.title}>Your Relationships Deserve a Real System</h1>
        <p className={styles.subtitle}>
          MyPeopleDatabase is a private, desktop-first contact manager that turns your messy address
          book into a structured knowledge base for every person in your life.
        </p>
        <div className={styles.heroCta}>
          <Link to="/buy" className={styles.primaryCta}>
            Get It Now
          </Link>
          <Link to="/features" className={styles.secondaryCta}>
            See All Features
          </Link>
        </div>

        {/* ── Core Features ── */}
        <p className={styles.sectionLabel}>What You Get</p>
        <h2 className={styles.sectionTitle}>Built for How You Actually Manage People</h2>
        <div className={styles.featureGrid}>
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>👥</div>
            <div className={styles.featureTitle}>Smart Groups</div>
            <p className={styles.featureDesc}>
              Organize contacts into nested, overlapping groups — family, work, church, sports
              league, neighborhood — and filter across all of them instantly.
            </p>
          </div>
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>🤖</div>
            <div className={styles.featureTitle}>AI-Powered Search</div>
            <p className={styles.featureDesc}>
              Built-in MCP server lets you query your contacts with natural language through your
              favorite AI tools. "Who do I know in Austin?" — answered in seconds.
            </p>
          </div>
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>🔒</div>
            <div className={styles.featureTitle}>100% Private</div>
            <p className={styles.featureDesc}>
              Your data lives on your machine. No cloud dependency, no social feed, no ads. You own
              every byte.
            </p>
          </div>
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>🔗</div>
            <div className={styles.featureTitle}>MPD ID</div>
            <p className={styles.featureDesc}>
              A single link + QR code that replaces the business card. Share it once — it stays
              current forever.
            </p>
          </div>
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>🧹</div>
            <div className={styles.featureTitle}>Merge Duplicates</div>
            <p className={styles.featureDesc}>
              Consolidate duplicate entries into a single clean record. No more three "John Smith"
              entries with different phone numbers.
            </p>
          </div>
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>🎂</div>
            <div className={styles.featureTitle}>Calendar Reminders</div>
            <p className={styles.featureDesc}>
              Never miss a birthday or anniversary again. See upcoming dates at a glance and stay
              connected to the people who matter.
            </p>
          </div>
        </div>

        {/* ── Use Cases ── */}
        <p className={styles.sectionLabel}>Who It's For</p>
        <h2 className={styles.sectionTitle}>Real People. Real Use Cases.</h2>
        <div className={styles.useCaseGrid}>
          <div className={styles.useCaseCard}>
            <div className={styles.useCaseWho}>Networkers &amp; Professionals</div>
            <div className={styles.useCaseTitle}>Turn contacts into relationships</div>
            <p className={styles.useCaseDesc}>
              You meet 20 people at a conference. Without context they're gone in a week. MPD stores
              who they are, where you met, and what to follow up on.
            </p>
          </div>
          <div className={styles.useCaseCard}>
            <div className={styles.useCaseWho}>Families</div>
            <div className={styles.useCaseTitle}>One place for everyone</div>
            <p className={styles.useCaseDesc}>
              Extended family, babysitters, doctors, coaches, neighbors — organized into groups with
              real notes instead of scattered across five apps.
            </p>
          </div>
          <div className={styles.useCaseCard}>
            <div className={styles.useCaseWho}>Community Leaders</div>
            <div className={styles.useCaseTitle}>Know your people</div>
            <p className={styles.useCaseDesc}>
              Pastors, club presidents, coaches — anyone who manages a community of people and needs
              to remember names, details, and connections.
            </p>
          </div>
          <div className={styles.useCaseCard}>
            <div className={styles.useCaseWho}>Privacy-Conscious Users</div>
            <div className={styles.useCaseTitle}>Own your data</div>
            <p className={styles.useCaseDesc}>
              Tired of platforms monetizing your relationships? MPD is local-first, private by
              default, and never sells your data.
            </p>
          </div>
        </div>

        {/* ── Differentiators ── */}
        <div className={styles.diffRow}>
          <div className={styles.diffItem}>
            <div className={styles.diffNumber}>0</div>
            <div className={styles.diffLabel}>Cloud accounts required</div>
          </div>
          <div className={styles.diffItem}>
            <div className={styles.diffNumber}>100%</div>
            <div className={styles.diffLabel}>Data ownership</div>
          </div>
          <div className={styles.diffItem}>
            <div className={styles.diffNumber}>Mac + Win</div>
            <div className={styles.diffLabel}>Desktop native</div>
          </div>
          <div className={styles.diffItem}>
            <div className={styles.diffNumber}>AI Ready</div>
            <div className={styles.diffLabel}>MCP server built in</div>
          </div>
        </div>

        {/* ── Quote ── */}
        <div className={styles.quoteBlock}>
          <p className={styles.quoteText}>
            "Your relationships are the most valuable asset you'll ever have. They deserve a real
            system — not a contacts app that was designed in 2005."
          </p>
          <div className={styles.quoteAttr}>— The MyPeopleDatabase Team</div>
        </div>

        {/* ── Bottom CTA ── */}
        <div className={styles.bottomCta}>
          <h2 className={styles.bottomCtaTitle}>Ready to organize the people in your life?</h2>
          <p className={styles.bottomCtaSub}>
            Available now for macOS and Windows. One-time purchase — no subscription required.
          </p>
          <div className={styles.heroCta}>
            <Link to="/buy" className={styles.primaryCta}>
              Buy Now
            </Link>
            <Link to="/download" className={styles.secondaryCta}>
              Download
            </Link>
          </div>
        </div>

        {/* ── Footer links ── */}
        <div className={styles.footerLinks}>
          <Link to="/features" className={styles.footerLink}>
            All Features
          </Link>
          <Link to="/features-groups" className={styles.footerLink}>
            Groups Deep-Dive
          </Link>
          <Link to="/features-ai" className={styles.footerLink}>
            AI Features
          </Link>
          <Link to="/vision" className={styles.footerLink}>
            Our Vision
          </Link>
          <Link to="/mpd-id" className={styles.footerLink}>
            MPD ID
          </Link>
          <Link to="/contact-us" className={styles.footerLink}>
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
}
